import React, { useState, useEffect, useCallback } from 'react';
import { moduleClientFactory } from '../api-service/module-client-factory';
import { GetDfdgMaintenanceModeStatusResponse } from '../api-service/clients/auto-generated-clients';

export interface DfdgMaintenanceModeStatus {
  isReadClosureActive: boolean;
  isWriteClosureActive: boolean;
  additionalInformation?: string;
  isActive: boolean;
  refresh: () => Promise<void>;
}

const defaultStatus: DfdgMaintenanceModeStatus = {
  isReadClosureActive: false,
  isWriteClosureActive: false,
  isActive: false,
  refresh: async () => {},
};

const DfdgMaintenanceModeContext = React.createContext<DfdgMaintenanceModeStatus>(defaultStatus);

type DfdgMaintenanceModeProviderProps = {
  children: React.ReactNode;
};

export function DfdgMaintenanceModeProvider({ children }: DfdgMaintenanceModeProviderProps) {
  const [status, setStatus] = useState<DfdgMaintenanceModeStatus>(defaultStatus);

  const fetchStatus = useCallback(async () => {
    try {
      const configurationClient = moduleClientFactory.createConfigurationClient();
      const result: GetDfdgMaintenanceModeStatusResponse = await configurationClient.getDfdgMaintenanceModeStatus();
      setStatus(prev => ({
        ...prev,
        isReadClosureActive: result.isReadClosureActive,
        isWriteClosureActive: result.isWriteClosureActive,
        additionalInformation: result.additionalInformation ?? undefined,
        isActive: result.isReadClosureActive || result.isWriteClosureActive,
      }));
    } catch {
      // Silently fail - maintenance status is non-critical
    }
  }, []);

  useEffect(() => {
    setStatus(prev => ({ ...prev, refresh: fetchStatus }));
    fetchStatus();
  }, [fetchStatus]);

  return <DfdgMaintenanceModeContext.Provider value={status}>{children}</DfdgMaintenanceModeContext.Provider>;
}

export const useDfdgMaintenanceMode = (): DfdgMaintenanceModeStatus => React.useContext(DfdgMaintenanceModeContext);
